import { NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatButtonModule, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatInput, MatInputModule, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { startWith } from 'rxjs';
import { map } from 'rxjs/operators';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';
import { TDataSourceAddEditPayload } from '../data-sources.interface';
import {
  EDataSourceAuthenticationStrategy,
  IDataSourceAddEditDialogData,
} from './data-sources-add-edit-dialog.interface';
import { DataSourcesAddEditDialogStore } from './data-sources-add-edit-dialog.store';

@Component({
  imports: [
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslatePipe,
    MatIcon,
    MatHint,
    NgIf,
    MatIconButton,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDropdown,
  ],
  providers: [DataSourcesAddEditDialogStore],
  selector: 'app-data-sources-add-edit-dialog',
  styleUrl: './data-sources-add-edit-dialog.component.scss',
  templateUrl: './data-sources-add-edit-dialog.component.html',
})
export class DataSourcesAddEditDialogComponent {
  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    restoreFocus: false,
    width: EDialogWidth.xSmall,
  };

  public readonly injectData = inject<IDataSourceAddEditDialogData>(MAT_DIALOG_DATA);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  public readonly store = inject(DataSourcesAddEditDialogStore);

  public readonly authenticationStrategyDropdownOptions = Object.values(EDataSourceAuthenticationStrategy).map(
    (type) => ({
      id: type,
      name: this.translate.instant(
        type === EDataSourceAuthenticationStrategy.none
          ? 'field.none'
          : `page.dataIntegrations.authenticationStrategyDropdownOption.${type}`,
      ),
    }),
  );

  public readonly authenticationTypeDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.authenticationStrategy'),
  };

  public readonly addEditForm = new FormGroup({
    apiBaseUrl: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    apiKey: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    apiSecret: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(255)],
    }),
    strategy: new FormControl(
      this.authenticationStrategyDropdownOptions.filter(({ id }) => id === EDataSourceAuthenticationStrategy.basic),
      {
        nonNullable: true,
        validators: [Validators.required],
      },
    ),
  });

  public readonly isInputMasked = {
    apiKey: true,
    apiSecret: true,
  };

  public readonly selectedAuthenticationType = toSignal(
    this.addEditForm.controls.strategy.valueChanges.pipe(
      map((value) => value[0]?.id ?? EDataSourceAuthenticationStrategy.none),
    ),
    { initialValue: EDataSourceAuthenticationStrategy.basic },
  );

  private readonly usedFieldsOfAuthenticationStrategies: Record<
    EDataSourceAuthenticationStrategy,
    ('apiKey' | 'apiSecret')[]
  > = {
    [EDataSourceAuthenticationStrategy.basic]: ['apiKey', 'apiSecret'],
    [EDataSourceAuthenticationStrategy.none]: [],
  };

  public readonly EDataSourceAuthenticationType = EDataSourceAuthenticationStrategy;

  constructor() {
    const { editData } = this.injectData;

    if (editData) {
      this.addEditForm.reset({
        apiBaseUrl: editData.apiBaseUrl,
        name: editData.name,
        strategy: this.authenticationStrategyDropdownOptions.filter(({ id }) => id === editData.authenticationStrategy),
      });
    }

    this.store.afterCreateUpdateSuccess$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.matDialogRef.close();
    });

    this.addEditForm.controls.strategy.valueChanges
      .pipe(startWith(this.addEditForm.controls.strategy.value), takeUntilDestroyed())
      .subscribe((value) => {
        if (!value.length) {
          return;
        }

        const usedInputs = this.usedFieldsOfAuthenticationStrategies[value[0].id];

        for (const input of ['apiKey', 'apiSecret'] as const) {
          if (usedInputs.includes(input)) {
            this.addEditForm.controls[input].enable();
          } else {
            this.addEditForm.controls[input].disable();
          }

          if (this.injectData.editData?.authenticationStrategy !== EDataSourceAuthenticationStrategy.basic) {
            this.addEditForm.controls[input].setValidators([
              Validators.required,
              TrimmedRequiredValidator(),
              Validators.maxLength(255),
            ]);
          } else {
            this.addEditForm.controls[input].setValidators([Validators.maxLength(255)]);
          }
        }
      });
  }

  public onFormDialogSubmit(): void {
    if (!this.addEditForm.valid) {
      return;
    }

    const payload = this.formatOnePayload();

    if (this.injectData.editData?.id) {
      this.store.updateOne({ dto: payload, id: this.injectData.editData.id });
    } else {
      this.store.createOne(payload);
    }
  }

  private formatOnePayload(): TDataSourceAddEditPayload {
    const { value } = this.addEditForm;

    return {
      apiBaseUrl: FormUtilitiesService.trimIfString(value.apiBaseUrl!),
      authentication: this.createAuthenticationPostData(),
      name: FormUtilitiesService.trimIfString(value.name!),
    };
  }

  private createAuthenticationPostData(): TDataSourceAddEditPayload['authentication'] {
    const strategy = this.addEditForm.controls.strategy.value[0].id;

    if (strategy === EDataSourceAuthenticationStrategy.none) {
      return null;
    }

    const allValues = {
      apiKey: FormUtilitiesService.trimIfString(this.addEditForm.value.apiKey),
      apiSecret: FormUtilitiesService.trimIfString(this.addEditForm.value.apiSecret),
    } as const;
    const usedFields = this.usedFieldsOfAuthenticationStrategies[strategy];

    return usedFields.reduce(
      (reducePayload, usedField) => ({
        ...reducePayload,
        [usedField]: allValues[usedField],
      }),
      { strategy },
    );
  }
}
